import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Box, InputAdornment,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { CustomCriterion } from '../../../types/admissions';

interface Props {
  open: boolean;
  applicationId: string;
  criteria: CustomCriterion[];
  existing: Record<string, number>;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}

/** Marks for the cycle's custom criteria (Hifz, sports, …). Clearing a
 *  field removes that mark. */
const CustomScoresDialog: React.FC<Props> = ({ open, applicationId, criteria, existing, onCancel, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(criteria.map(c => [c.key, existing?.[c.key] != null ? String(existing[c.key]) : ''])));

  const errorFor = (c: CustomCriterion) => {
    const v = values[c.key] ?? '';
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= c.maxMarks ? null : `0 – ${c.maxMarks}`;
  };
  const invalid = criteria.some(c => errorFor(c));

  const save = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.recordCustomScores(applicationId, Object.fromEntries(
        criteria.map(c => [c.key, (values[c.key] ?? '').trim() === '' ? null : Number(values[c.key])]),
      ));
      enqueueSnackbar('Marks saved', { variant: 'success' });
      await onSaved();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the marks', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>Additional criteria</Typography>
        <Typography variant="body2" color="text.secondary">Counted toward merit as set in the cycle.</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 2 }}>
          {criteria.map(c => (
            <Stack key={c.key} direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
                <Typography variant="body2" fontWeight={600}>{c.label}</Typography>
                {c.description && <Typography variant="caption" color="text.secondary">{c.description}</Typography>}
              </Box>
              <TextField
                size="small"
                value={values[c.key] ?? ''}
                placeholder="—"
                onChange={(e) => setValues(v => ({ ...v, [c.key]: e.target.value.replace(/[^\d.]/g, '') }))}
                error={!!errorFor(c)}
                helperText={errorFor(c) || ' '}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                InputProps={{ endAdornment: <InputAdornment position="end">/ {c.maxMarks}</InputAdornment> }}
                sx={{ width: 150 }}
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || invalid}>
          {busy ? 'Saving…' : 'Save marks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomScoresDialog;
